import { useEffect, useState } from "react";

/**
 * Light, dark, or whatever the operating system says.
 *
 * The console is dark first (the Clearway design): a browser that has never chosen opens dark.
 * `system` follows `prefers-color-scheme` and keeps following it while the page is open, so a
 * laptop that switches at sunset takes the console with it.
 *
 * Stored under `fg-theme`, the key the Clearway site and identity pages use, so a choice means the
 * same thing wherever it was made. Per origin, as all browser storage is: it is a convenience, and
 * the page renders correctly without it.
 */
export type ThemeMode = "system" | "light" | "dark";

const KEY = "fg-theme";
const DEFAULT_MODE: ThemeMode = "dark";

export function getThemeMode(): ThemeMode {
    try {
        const v = window.localStorage.getItem(KEY);
        if (v === "system" || v === "light" || v === "dark") return v;
    } catch {
        /* storage unavailable: the default stands */
    }
    return DEFAULT_MODE;
}

/** Dark unless the OS positively asks for light: the console is dark first, so "no preference"
 *  and "cannot tell" both resolve to dark. index.html's pre-paint script resolves it the same way. */
function systemDark(): boolean {
    return !window.matchMedia?.("(prefers-color-scheme: light)").matches;
}

/** Stamps the resolved theme on <html>: the `dark` class Tailwind keys on, and `data-theme`. */
export function applyTheme(mode: ThemeMode = getThemeMode()) {
    const dark = mode === "dark" || (mode === "system" && systemDark());
    const root = document.documentElement;
    root.classList.toggle("dark", dark);
    root.setAttribute("data-theme", dark ? "dark" : "light");
}

export function setThemeMode(mode: ThemeMode) {
    try {
        window.localStorage.setItem(KEY, mode);
    } catch {
        /* nicety */
    }
    applyTheme(mode);
    window.dispatchEvent(new CustomEvent("fg-theme", { detail: mode }));
}

/** The current mode, and a setter; re-applies when the OS flips while on `system`. */
export function useThemeMode(): [ThemeMode, (m: ThemeMode) => void] {
    const [mode, setMode] = useState<ThemeMode>(getThemeMode);

    useEffect(() => {
        const onChange = () => setMode(getThemeMode());
        window.addEventListener("fg-theme", onChange);
        return () => window.removeEventListener("fg-theme", onChange);
    }, []);

    useEffect(() => {
        if (mode !== "system" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-color-scheme: light)");
        const onChange = () => applyTheme("system");
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, [mode]);

    return [mode, setThemeMode];
}
